import { useCallback, useEffect, useState } from "react";
import { Button, Input, Select } from "../../../ui";
import { formatSar, sarToMinorUnits } from "../../../../utils/billingMoney";
import { getBillingSettingsAPI, updateBillingSettingsAPI } from "../../../../utils/api/billing";
import {
  adminActivateAiPricingAPI,
  adminCreateAiPricingAPI,
  adminGetAiChargeAPI,
  adminGetAiCostSummaryAPI,
  adminListAiChargesAPI,
  adminListAiPricingAPI,
  adminListAiReconciliationAPI,
  adminListAiTopUpsAPI,
  adminRefundAiUsageAPI,
  adminResolveAiReconciliationAPI,
  newIdempotencyKey,
} from "../../../../utils/api/aiWallet";
import AiWalletActionModal from "./AiWalletActionModal";
import { STATE_BADGE, formatWalletDate } from "../aiWalletText";

const usd = (value) => (value === null || value === undefined ? "-" : `$${value}`);
const sarInput = (minor) => (Number(minor || 0) / 100).toFixed(2);

function useLoader(loader, deps) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await loader());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => {
    load();
  }, [load]);
  return { data, error, loading, reload: load, setError };
}

// --------------------------------------------------------------- top-ups

// Top-up requests of every restaurant. Approving the payment stays in the
// payment review of the Invoices tab; this list shows where each one stands.
export const AiWalletAdminTopUps = ({ text, lang, onReviewPayments }) => {
  const t = text.admin;
  const [state, setState] = useState("");
  const { data, error, loading } = useLoader(() => adminListAiTopUpsAPI({ state, limit: 50 }), [state]);
  const states = ["", "awaiting_payment", "finance_review", "credited", "cancelled", "expired"];
  return (
    <div className="aiw-admin">
      <div className="aiw-toolbar">
        <Select label={t.stateFilter} value={state} onChange={(event) => setState(event.target.value)} options={states.map((value) => ({ value, label: value ? text.states[value] : t.all }))} />
      </div>
      {error && <div className="billing-error">{error}</div>}
      {loading && !data && <div className="billing-loading">{text.loading}</div>}
      {data && (
        <div className="aiw-table-wrap">
          <table className="aiw-table">
            <thead>
              <tr>
                <th>{text.colDate}</th>
                <th>{t.colRestaurant}</th>
                <th>{text.invoice}</th>
                <th className="num">{text.credit}</th>
                <th className="num">{text.toPay}</th>
                <th>{text.colStatus}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {!data.topUps.length && (
                <tr>
                  <td colSpan={7}>{text.empty}</td>
                </tr>
              )}
              {data.topUps.map((topUp) => (
                <tr key={topUp._id}>
                  <td>{formatWalletDate(topUp.createdAt, lang)}</td>
                  <td>
                    {topUp.storeId}
                    {topUp.requestedBy?.name && <small>{topUp.requestedBy.name}</small>}
                  </td>
                  <td>{topUp.invoiceNumber || "-"}</td>
                  <td className="num">{formatSar(topUp.creditMinor, lang)}</td>
                  <td className="num">{formatSar(topUp.payableMinor, lang)}</td>
                  <td>
                    <span className={STATE_BADGE[topUp.state] || "badge-neutral"}>{text.states[topUp.state] || topUp.state}</span>
                  </td>
                  <td>
                    {topUp.pendingPayment && (
                      <Button size="sm" appearance="outline" onClick={onReviewPayments}>
                        {t.reviewPayment}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// --------------------------------------------------------- usage and cost

// The internal economics of AI usage: what the provider cost, what the
// customer was (or, in shadow mode, would have been) charged, and the margin.
export const AiWalletAdminCharges = ({ text, lang, access }) => {
  const t = text.admin;
  const [storeId, setStoreId] = useState("");
  const [mode, setMode] = useState("");
  const [detail, setDetail] = useState(null);
  const [refund, setRefund] = useState(null);
  const summary = useLoader(() => adminGetAiCostSummaryAPI({ storeId, mode, groupBy: "feature" }), [storeId, mode]);
  const charges = useLoader(() => adminListAiChargesAPI({ storeId, mode, limit: 50 }), [storeId, mode]);
  const totals = (summary.data?.rows || []).reduce((sum, row) => ({ cost: sum.cost + row.providerCostMinor, debited: sum.debited + row.netRevenueMinor, computed: sum.computed + row.computedChargeMinor, margin: sum.margin + row.marginMinor }), { cost: 0, debited: 0, computed: 0, margin: 0 });

  const open = async (charge) => {
    try {
      setDetail(await adminGetAiChargeAPI(charge._id));
    } catch (err) {
      charges.setError(err.message);
    }
  };
  const doRefund = async (values) => {
    await adminRefundAiUsageAPI(refund.charge.storeId, { chargeId: refund.charge._id, amountMinor: values.amountMinor, reason: values.reason, idempotencyKey: refund.key });
    setRefund(null);
    setDetail(await adminGetAiChargeAPI(refund.charge._id));
    charges.reload();
    summary.reload();
  };

  return (
    <div className="aiw-admin">
      <div className="aiw-toolbar">
        <Input label={t.storeIdPlaceholder} value={storeId} onChange={(event) => setStoreId(event.target.value.trim())} />
        <Select label={t.colMode} value={mode} onChange={(event) => setMode(event.target.value)} options={[{ value: "", label: t.all }, { value: "enforce", label: t.modes.enforce }, { value: "shadow", label: t.modes.shadow }]} />
      </div>
      {(charges.error || summary.error) && <div className="billing-error">{charges.error || summary.error}</div>}
      <div className="aiw-kpis">
        <div className="aiw-kpi">
          <span>{t.summaryProviderCost}</span>
          <strong>{formatSar(totals.cost, lang)}</strong>
        </div>
        <div className="aiw-kpi">
          <span>{t.summaryCharge}</span>
          <strong>{formatSar(totals.debited, lang)}</strong>
        </div>
        <div className="aiw-kpi">
          <span>{t.summaryMargin}</span>
          <strong>{formatSar(totals.margin, lang)}</strong>
        </div>
        <div className="aiw-kpi">
          <span>{t.summaryShadow}</span>
          <strong>{formatSar(totals.computed, lang)}</strong>
        </div>
      </div>
      {charges.data && (
        <div className="aiw-table-wrap">
          <table className="aiw-table">
            <thead>
              <tr>
                <th>{text.colDate}</th>
                <th>{t.colRestaurant}</th>
                <th>{t.colFeature}</th>
                <th>{t.colMode}</th>
                <th>{t.colBillable}</th>
                <th className="num">{t.colProviderCost}</th>
                <th className="num">{t.colComputed}</th>
                <th className="num">{t.colDebited}</th>
                <th className="num">{t.colMargin}</th>
              </tr>
            </thead>
            <tbody>
              {!charges.data.charges.length && (
                <tr>
                  <td colSpan={9}>{text.empty}</td>
                </tr>
              )}
              {charges.data.charges.map((charge) => (
                <tr key={charge._id} className="clickable" onClick={() => open(charge)}>
                  <td>{formatWalletDate(charge.createdAt, lang)}</td>
                  <td>
                    {charge.storeId}
                    {charge.initiatedBy?.name && <small>{charge.initiatedBy.name}</small>}
                  </td>
                  <td>
                    {text.features[charge.feature] || charge.feature}
                    <small>{charge.status}</small>
                  </td>
                  <td>{t.modes[charge.mode] || charge.mode}</td>
                  <td>{charge.outcome?.billable === null || charge.outcome?.billable === undefined ? "-" : charge.outcome.billable ? t.yes : t.no}</td>
                  <td className="num" dir="ltr">
                    {usd(charge.usage.providerCostUsd)}
                    {charge.providerCostMinor !== null && <small>{formatSar(charge.providerCostMinor, lang)}</small>}
                  </td>
                  <td className="num">{formatSar(charge.computedChargeMinor, lang)}</td>
                  <td className="num">
                    {formatSar(charge.debitedMinor, lang)}
                    {charge.refundedMinor > 0 && <small>{t.colRefunded}: {formatSar(charge.refundedMinor, lang)}</small>}
                    {charge.shortfallMinor > 0 && <small>{t.colShortfall}: {formatSar(charge.shortfallMinor, lang)}</small>}
                  </td>
                  <td className="num">{charge.marginMinor === null ? "-" : formatSar(charge.marginMinor, lang)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detail && (
        <div className="billing-modal-backdrop" dir={lang === "en" ? "ltr" : "rtl"}>
          <div className="billing-modal billing-modal-wide" role="dialog" aria-modal="true" aria-label={t.chargeDetail}>
            <h3>
              {t.chargeDetail}: {text.features[detail.charge.feature] || detail.charge.feature}
            </h3>
            <p dir="ltr">{detail.charge.chargeKey}</p>
            <div className="aiw-kpis">
              <div className="aiw-kpi">
                <span>{t.colProviderCost}</span>
                <strong dir="ltr">{usd(detail.charge.usage.providerCostUsd)}</strong>
              </div>
              <div className="aiw-kpi">
                <span>{t.colCustomerCharge}</span>
                <strong>{formatSar(detail.charge.mode === "enforce" ? detail.charge.debitedMinor : detail.charge.computedChargeMinor, lang)}</strong>
              </div>
              <div className="aiw-kpi">
                <span>{t.colMargin}</span>
                <strong>{detail.charge.marginMinor === null ? "-" : formatSar(detail.charge.marginMinor, lang)}</strong>
              </div>
            </div>
            {detail.charge.pricing && (
              <div className="billing-preview-box">
                <div>
                  <span>{t.pricingSnapshot}</span>
                  <strong>
                    {t.version} {detail.charge.pricing.version}
                    {detail.charge.pricing.provisional ? ` · ${t.provisional}` : ""}
                  </strong>
                </div>
                <div>
                  <span>{t.rate}</span>
                  <strong>{detail.charge.pricing.usdToSarRate}</strong>
                </div>
                <div>
                  <span>{t.markup}</span>
                  <strong>{detail.charge.pricing.markup}</strong>
                </div>
                <div>
                  <span>{t.chargeBasis}</span>
                  <strong>{t.bases[detail.charge.pricing.chargeBasis] || detail.charge.pricing.chargeBasis}</strong>
                </div>
              </div>
            )}
            <div className="aiw-table-wrap">
              <table className="aiw-table">
                <thead>
                  <tr>
                    <th>{t.colRequests}</th>
                    <th>{t.colModel}</th>
                    <th>{text.colStatus}</th>
                    <th className="num">{t.colTokens}</th>
                    <th className="num">{t.colCost}</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.requests.map((request) => (
                    <tr key={request._id}>
                      <td>
                        {request.purpose || request.task}
                        <small>
                          {request.role} · {request.reason}
                        </small>
                      </td>
                      <td dir="ltr">
                        {request.actualModel || request.requestedModel}
                        {request.provider && <small>{request.provider}</small>}
                      </td>
                      <td>
                        {request.status}
                        <small>{request.errorCode || request.billing}</small>
                      </td>
                      <td className="num">{request.totalTokens ?? "-"}</td>
                      <td className="num" dir="ltr">
                        {usd(request.costUsd)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="billing-modal-actions">
              {access?.canRefundAiUsage && detail.charge.mode === "enforce" && detail.charge.status === "settled" && detail.charge.debitedMinor > detail.charge.refundedMinor && (
                <Button appearance="outline" onClick={() => setRefund({ charge: detail.charge, key: newIdempotencyKey("refund") })}>
                  {t.refund}
                </Button>
              )}
              <Button appearance="ghost" onClick={() => setDetail(null)}>
                {text.cancel}
              </Button>
            </div>
          </div>
        </div>
      )}
      {refund && (
        <AiWalletActionModal title={`${t.refund} (≤ ${formatSar(refund.charge.debitedMinor - refund.charge.refundedMinor, lang)})`} fields={{ amount: true }} maxMinor={refund.charge.debitedMinor - refund.charge.refundedMinor} text={text} lang={lang} onSubmit={doRefund} onCancel={() => setRefund(null)} />
      )}
    </div>
  );
};

// ----------------------------------------------------------- reconciliation

export const AiWalletAdminReconciliation = ({ text, lang, access }) => {
  const t = text.admin;
  const { data, error, loading, reload } = useLoader(() => adminListAiReconciliationAPI({ limit: 50 }), []);
  const [resolving, setResolving] = useState(null);
  const resolve = async (values) => {
    await adminResolveAiReconciliationAPI(resolving.item._id, { status: resolving.status, ...(resolving.status === "confirmed_billed" ? { confirmedCostUsd: values.cost } : {}), note: values.note });
    setResolving(null);
    reload();
  };
  return (
    <div className="aiw-admin">
      {error && <div className="billing-error">{error}</div>}
      {loading && !data && <div className="billing-loading">{text.loading}</div>}
      {data && (
        <>
          <div className="aiw-panel">
            <h4>{t.unknownCosts}</h4>
            <p className="aiw-muted">{t.unknownCostsNote}</p>
            {!data.unknownCosts.length ? (
              <div className="billing-empty-note">{t.nothing}</div>
            ) : (
              <div className="aiw-table-wrap">
                <table className="aiw-table">
                  <thead>
                    <tr>
                      <th>{text.colDate}</th>
                      <th>{t.colRestaurant}</th>
                      <th>{t.colModel}</th>
                      <th>{text.colStatus}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {data.unknownCosts.map((item) => (
                      <tr key={item._id}>
                        <td>{formatWalletDate(item.startedAt, lang)}</td>
                        <td>
                          {item.storeId || "-"}
                          <small>{text.features[item.feature] || item.feature}</small>
                        </td>
                        <td dir="ltr">
                          {item.requestedModel}
                          <small>{item.errorCode}</small>
                        </td>
                        <td>{item.status}</td>
                        <td>
                          {access?.canResolveAiReconciliation && item.status === "pending" && (
                            <div className="aiw-row-actions">
                              <Button size="sm" appearance="outline" onClick={() => setResolving({ item, status: "confirmed_billed" })}>
                                {t.resolveBilled}
                              </Button>
                              <Button size="sm" appearance="outline" onClick={() => setResolving({ item, status: "confirmed_unbilled" })}>
                                {t.resolveUnbilled}
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="aiw-panel">
            <h4>{t.shortfalls}</h4>
            {!data.shortfalls.length ? (
              <div className="billing-empty-note">{t.nothing}</div>
            ) : (
              data.shortfalls.map((charge) => (
                <div key={charge._id} className="aiw-muted">
                  {charge.storeId} · {text.features[charge.feature] || charge.feature} · {t.colComputed} {formatSar(charge.computedChargeMinor, lang)} · {t.colDebited} {formatSar(charge.debitedMinor, lang)} · {t.colShortfall} {formatSar(charge.shortfallMinor, lang)}
                </div>
              ))
            )}
          </div>
          <div className="aiw-panel">
            <h4>{t.unsettled}</h4>
            <p className="aiw-muted">{t.unsettledNote}</p>
            {!(data.unsettledCharges || []).length ? (
              <div className="billing-empty-note">{t.nothing}</div>
            ) : (
              data.unsettledCharges.map((charge) => (
                <div key={charge._id} className="aiw-muted">
                  {charge.storeId} · {text.features[charge.feature] || charge.feature} · {formatWalletDate(charge.createdAt, lang)} · <span dir="ltr">{charge.chargeKey}</span>
                  {charge.settlementError?.code ? <span dir="ltr"> · {charge.settlementError.code}</span> : null}
                </div>
              ))
            )}
          </div>
          <div className="aiw-panel">
            <h4>{t.unposted}</h4>
            {!data.unpostedEntries.length ? (
              <div className="billing-empty-note">{t.nothing}</div>
            ) : (
              data.unpostedEntries.map((entry) => (
                <div key={entry._id} className="aiw-muted" dir="ltr">
                  {entry.storeId} · {entry.type} · {formatSar(entry.amountMinor, lang)} · {entry.idempotencyKey}
                </div>
              ))
            )}
          </div>
        </>
      )}
      {resolving && <AiWalletActionModal title={resolving.status === "confirmed_billed" ? t.resolveBilled : t.resolveUnbilled} fields={{ cost: resolving.status === "confirmed_billed", note: true }} text={text} lang={lang} onSubmit={resolve} onCancel={() => setResolving(null)} />}
    </div>
  );
};

// ------------------------------------------------------ pricing and policy

const HOLD_TYPES = ["copilot_message", "copilot_regenerate", "import_text", "import_image", "import_pdf"];
const EMPTY_RULE = { usdToSarRate: "", markup: "", fixedFeeSar: "0.00", minChargeSar: "0.00", chargeBasis: "succeeded_attempts", holdsSar: { copilot_message: "0.50", copilot_regenerate: "0.50", import_text: "3.00", import_image: "3.00", import_pdf: "6.00" } };

// Pricing rule versions (rate, markup, fees, holds) and the top-up policy.
// Nothing has a built-in price: a rule must be created and activated.
export const AiWalletAdminPricing = ({ text, lang, access }) => {
  const t = text.admin;
  const token = localStorage.getItem("auth-token");
  const canManage = Boolean(access?.canManageAiPricing);
  const canSettings = Boolean(access?.canManageBillingSettings);
  const rules = useLoader(() => adminListAiPricingAPI(), []);
  const [form, setForm] = useState(EMPTY_RULE);
  const [policy, setPolicy] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getBillingSettingsAPI(token)
      .then((result) => {
        const wallet = result.data?.aiWallet || {};
        setPolicy({ minTopUpSar: sarInput(wallet.minTopUpMinor ?? 2000), maxTopUpSar: sarInput(wallet.maxTopUpMinor ?? 500000), topUpExpiryDays: String(wallet.topUpExpiryDays ?? 7), lowBalanceSar: sarInput(wallet.lowBalanceThresholdMinor ?? 500) });
      })
      .catch((err) => setError(err.message));
  }, [token]);

  const run = async (fn) => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await fn();
      setMessage(t.saved);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };
  const createRule = () =>
    run(async () => {
      await adminCreateAiPricingAPI(form);
      setForm(EMPTY_RULE);
      await rules.reload();
    });
  const activate = (rule) =>
    run(async () => {
      await adminActivateAiPricingAPI(rule._id);
      await rules.reload();
    });
  // Amounts are parsed as text into halalas (no float arithmetic).
  const savePolicy = () => run(() => updateBillingSettingsAPI(token, { aiWallet: { minTopUpMinor: sarToMinorUnits(policy.minTopUpSar), maxTopUpMinor: sarToMinorUnits(policy.maxTopUpSar), topUpExpiryDays: Number(policy.topUpExpiryDays), lowBalanceThresholdMinor: sarToMinorUnits(policy.lowBalanceSar) } }));

  return (
    <div className="aiw-admin">
      {(error || rules.error) && <div className="billing-error">{error || rules.error}</div>}
      {message && <div className="billing-success">{message}</div>}

      <div className="aiw-panel">
        <h4>{t.pricingRules}</h4>
        <p className="aiw-muted">{t.pricingNote}</p>
        <div className="aiw-table-wrap">
          <table className="aiw-table">
            <thead>
              <tr>
                <th>{t.version}</th>
                <th>{text.colStatus}</th>
                <th className="num">{t.rate}</th>
                <th className="num">{t.markup}</th>
                <th className="num">{t.fixedFee}</th>
                <th className="num">{t.minCharge}</th>
                <th>{t.chargeBasis}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {!(rules.data || []).length && (
                <tr>
                  <td colSpan={8}>{text.empty}</td>
                </tr>
              )}
              {(rules.data || []).map((rule) => (
                <tr key={rule._id}>
                  <td>
                    {rule.version}
                    {rule.provisional && <small>{t.provisional}</small>}
                  </td>
                  <td>
                    <span className={rule.status === "active" ? "badge-success" : rule.status === "draft" ? "badge-warning" : "badge-neutral"}>{t.ruleStatus[rule.status] || rule.status}</span>
                  </td>
                  <td className="num">{rule.fx.usdToSarRate}</td>
                  <td className="num">{rule.defaults.markup}</td>
                  <td className="num">{rule.defaults.fixedFeeSar}</td>
                  <td className="num">{rule.defaults.minChargeSar}</td>
                  <td>{t.bases[rule.defaults.chargeBasis] || rule.defaults.chargeBasis}</td>
                  <td>
                    {canManage && rule.status === "draft" && (
                      <Button size="sm" appearance="outline" disabled={saving} onClick={() => activate(rule)}>
                        {t.activate}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {canManage && (
        <div className="aiw-panel">
          <h4>{t.newRule}</h4>
          <div className="aiw-form-grid">
            <Input label={t.rate} inputMode="decimal" value={form.usdToSarRate} onChange={(event) => setForm({ ...form, usdToSarRate: event.target.value.trim() })} />
            <Input label={t.markup} inputMode="decimal" value={form.markup} onChange={(event) => setForm({ ...form, markup: event.target.value.trim() })} />
            <Input label={t.fixedFee} inputMode="decimal" value={form.fixedFeeSar} onChange={(event) => setForm({ ...form, fixedFeeSar: event.target.value.trim() })} />
            <Input label={t.minCharge} inputMode="decimal" value={form.minChargeSar} onChange={(event) => setForm({ ...form, minChargeSar: event.target.value.trim() })} />
            <Select label={t.chargeBasis} value={form.chargeBasis} onChange={(event) => setForm({ ...form, chargeBasis: event.target.value })} options={Object.entries(t.bases).map(([value, label]) => ({ value, label }))} />
          </div>
          <strong>{t.holdsTitle}</strong>
          <div className="aiw-form-grid">
            {HOLD_TYPES.map((type) => (
              <Input key={type} label={t.holdLabels[type]} inputMode="decimal" value={form.holdsSar[type]} onChange={(event) => setForm({ ...form, holdsSar: { ...form.holdsSar, [type]: event.target.value.trim() } })} />
            ))}
          </div>
          <div>
            <Button appearance="filled" disabled={saving || !form.usdToSarRate || !form.markup} onClick={createRule}>
              {t.createRule}
            </Button>
          </div>
        </div>
      )}

      {policy && (
        <div className="aiw-panel">
          <h4>{t.policyTitle}</h4>
          <div className="aiw-form-grid">
            <Input label={t.minTopUpSar} inputMode="decimal" disabled={!canSettings} value={policy.minTopUpSar} onChange={(event) => setPolicy({ ...policy, minTopUpSar: event.target.value.trim() })} />
            <Input label={t.maxTopUpSar} inputMode="decimal" disabled={!canSettings} value={policy.maxTopUpSar} onChange={(event) => setPolicy({ ...policy, maxTopUpSar: event.target.value.trim() })} />
            <Input label={t.expiryDays} inputMode="numeric" disabled={!canSettings} value={policy.topUpExpiryDays} onChange={(event) => setPolicy({ ...policy, topUpExpiryDays: event.target.value.trim() })} />
            <Input label={t.lowBalanceSar} inputMode="decimal" disabled={!canSettings} value={policy.lowBalanceSar} onChange={(event) => setPolicy({ ...policy, lowBalanceSar: event.target.value.trim() })} />
          </div>
          {canSettings && (
            <div>
              <Button appearance="filled" disabled={saving} onClick={savePolicy}>
                {t.save}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
